/**
 * Screening analyses (framework-aligned).
 *
 * Geometry readiness/massing and sun/shadow screening, normalized to the
 * canonical `simulation-evidence/v1` contract so they flow through the same
 * report path as engine-produced evidence. Outputs are technical screening
 * evidence only and never assert municipal compliance.
 */

import type {
  EvidenceFinding,
  EvidenceMetric,
  EvidenceStatus,
  SimulationEvidence,
} from './contracts';

import type {
  FormaAnalysisGroundGrid,
  FormaAnalysisRecord,
  FormaSunAnalysis,
  FormaSunAnalysisParameters,
  GeometryExchange,
  SunAnalysisResult,
} from './index';

const SCREENING_ONLY_FINDING: EvidenceFinding = {
  code: 'screening-only',
  severity: 'info',
  message: 'Output is technical screening evidence only and does not assert municipal compliance.',
};

/** Geometry readiness / massing pre-check normalized as screening evidence. */
export function analyzeGeometryReadiness(exchange: GeometryExchange): SimulationEvidence {
  const missingInputs = uniqueStrings([
    ...exchange.summary.missingData,
    ...exchange.elements.flatMap((element) => element.missingData),
  ]);

  return {
    schemaVersion: 'simulation-evidence/v1',
    studyId: 'geometry-readiness-massing',
    source: exchange.source === 'fixture' ? 'fixture' : 'forma-native',
    status: getGeometryReadinessStatus(exchange, missingInputs),
    confidence: exchange.summary.confidence,
    metrics: buildGeometryMassingMetrics(exchange),
    findings: buildGeometryReadinessFindings(exchange, missingInputs),
    missingInputs,
  };
}

/** Sun/shadow screening over a Forma-native sun analysis, normalized as evidence. */
export function analyzeSunShadowScreening(result: SunAnalysisResult): SimulationEvidence {
  const parameters = isFormaSunAnalysis(result.analysis) ? result.analysis.parameters : undefined;
  const missingInputs = buildSunShadowMissingInputs(result, parameters);
  const isBlocked =
    result.analysis.status === 'FAILED' ||
    missingInputs.includes('sunAnalysisType') ||
    missingInputs.includes('sunParameters') ||
    missingInputs.includes('selectedElementPaths');
  const status: EvidenceStatus = isBlocked
    ? 'blocked'
    : result.analysis.status !== 'SUCCEEDED' || missingInputs.length > 0
      ? 'needs-review'
      : 'screening-ready';

  return {
    schemaVersion: 'simulation-evidence/v1',
    studyId: 'sun-shadow-screening',
    source: 'forma-native',
    status,
    confidence:
      result.analysis.status === 'SUCCEEDED' && missingInputs.length === 0
        ? 'high'
        : result.analysis.status === 'FAILED' || missingInputs.includes('sunParameters')
          ? 'low'
          : 'medium',
    metrics: buildSunShadowMetrics(result.groundGrid, parameters),
    findings: buildSunShadowFindings(result, missingInputs),
    missingInputs,
  };
}

function getGeometryReadinessStatus(
  exchange: GeometryExchange,
  missingInputs: readonly string[],
): EvidenceStatus {
  if (exchange.summary.buildingCount === 0 || exchange.elements.length === 0) {
    return 'blocked';
  }

  if (exchange.summary.confidence !== 'high' || missingInputs.length > 0) {
    return 'needs-review';
  }

  return 'screening-ready';
}

function buildGeometryMassingMetrics(exchange: GeometryExchange): EvidenceMetric[] {
  const summary = exchange.summary;
  const metrics: EvidenceMetric[] = [
    { label: 'Buildings', value: summary.buildingCount },
    { label: 'Footprints', value: summary.footprintCount },
    {
      label: 'Footprint coverage',
      value:
        summary.buildingCount === 0 ? 0 : roundTwo(summary.footprintCount / summary.buildingCount),
    },
    { label: 'Confidence', value: summary.confidence },
  ];
  const totalFootprintAreaM2 = sumFinite(
    exchange.elements.map((element) => element.footprint?.areaM2),
  );

  if (totalFootprintAreaM2 !== undefined) {
    metrics.push({ label: 'Footprint area', value: roundTwo(totalFootprintAreaM2), unit: 'm2' });
  }

  if (summary.grossFloorAreaM2 !== undefined) {
    metrics.push({ label: 'Gross floor area', value: summary.grossFloorAreaM2, unit: 'm2' });
  }

  const heightRange =
    summary.heightRangeMeters ??
    getNumberRange(exchange.elements.map((element) => element.heightMeters));

  if (heightRange !== undefined) {
    metrics.push({ label: 'Height range', value: formatRange(heightRange), unit: 'm' });
  }

  const storyRange = getNumberRange(exchange.elements.map((element) => element.storiesAboveGrade));

  if (storyRange !== undefined) {
    metrics.push({ label: 'Stories range', value: formatRange(storyRange) });
  }

  return metrics;
}

function buildGeometryReadinessFindings(
  exchange: GeometryExchange,
  missingInputs: readonly string[],
): EvidenceFinding[] {
  const findings: EvidenceFinding[] = [SCREENING_ONLY_FINDING];

  if (exchange.summary.buildingCount === 0 || exchange.elements.length === 0) {
    findings.push({
      code: 'no-building-geometry',
      severity: 'blocker',
      message: 'No building geometry is available for a geometry pre-check.',
    });
  }

  if (exchange.source === 'fixture') {
    findings.push({
      code: 'fixture-source',
      severity: 'info',
      message: 'Geometry evidence comes from a deterministic fixture, not a live Forma project.',
    });
  }

  if (exchange.summary.confidence !== 'high') {
    findings.push({
      code: 'geometry-confidence',
      severity: 'warning',
      message: `Geometry confidence is ${exchange.summary.confidence}; review before client use.`,
    });
  }

  for (const input of missingInputs) {
    findings.push({
      code: `missing-${input}`,
      severity: 'warning',
      message: `Geometry input '${input}' is missing or synthetic.`,
    });
  }

  return findings;
}

function isFormaSunAnalysis(analysis: FormaAnalysisRecord): analysis is FormaSunAnalysis {
  return (
    analysis.analysisType === 'sun' &&
    typeof analysis.parameters === 'object' &&
    analysis.parameters !== null
  );
}

function buildSunShadowMissingInputs(
  result: SunAnalysisResult,
  parameters?: FormaSunAnalysisParameters,
): string[] {
  const missingInputs: string[] = [];

  if (result.analysis.analysisType !== 'sun') {
    missingInputs.push('sunAnalysisType');
  }

  if (result.analysis.status === 'FAILED') {
    missingInputs.push('succeededSunAnalysis');
  }

  if (parameters === undefined) {
    missingInputs.push('sunParameters');

    return missingInputs;
  }

  if (parameters.selectedElementPaths.length === 0) {
    missingInputs.push('selectedElementPaths');
  }

  if (parameters.sunDate.month < 1 || parameters.sunDate.month > 12) {
    missingInputs.push('sunMonth');
  }

  if (parameters.sunDate.date < 1 || parameters.sunDate.date > 31) {
    missingInputs.push('sunDate');
  }

  if (parameters.timezone.length === 0) {
    missingInputs.push('timezone');
  }

  if (result.groundGrid === undefined) {
    missingInputs.push('sunGroundGrid');
  }

  return missingInputs;
}

function buildSunShadowMetrics(
  groundGrid?: FormaAnalysisGroundGrid,
  parameters?: FormaSunAnalysisParameters,
): EvidenceMetric[] {
  const metrics: EvidenceMetric[] = [
    { label: 'Selected elements', value: parameters?.selectedElementPaths.length ?? 0 },
  ];

  if (parameters !== undefined) {
    metrics.push({ label: 'Month', value: parameters.sunDate.month });
    metrics.push({ label: 'Date', value: parameters.sunDate.date });
  }

  if (groundGrid === undefined) {
    return metrics;
  }

  metrics.push({ label: 'Grid cells', value: groundGrid.width * groundGrid.height });
  metrics.push({ label: 'Grid resolution', value: groundGrid.resolution, unit: 'm' });

  if (groundGrid.mask !== undefined) {
    const covered = groundGrid.mask.reduce((sum, value) => sum + (value > 0 ? 1 : 0), 0);
    metrics.push({ label: 'Covered grid cells', value: covered });
  }

  const values = Array.from(groundGrid.grid).filter(
    (_value, index) => groundGrid.mask?.[index] !== 0,
  );

  if (values.length > 0) {
    metrics.push({
      label: 'Mean grid value',
      value: roundTwo(values.reduce((sum, value) => sum + value, 0) / values.length),
    });
    metrics.push({ label: 'Minimum grid value', value: Math.min(...values) });
    metrics.push({ label: 'Maximum grid value', value: Math.max(...values) });
  }

  return metrics;
}

function buildSunShadowFindings(
  result: SunAnalysisResult,
  missingInputs: readonly string[],
): EvidenceFinding[] {
  const findings: EvidenceFinding[] = [SCREENING_ONLY_FINDING];

  if (result.analysis.status !== 'SUCCEEDED') {
    findings.push({
      code: 'sun-analysis-status',
      severity: result.analysis.status === 'FAILED' ? 'blocker' : 'warning',
      message: `Forma sun analysis status is ${result.analysis.status}.`,
    });
  }

  for (const input of missingInputs) {
    findings.push({
      code: `missing-${input}`,
      severity: input === 'sunGroundGrid' ? 'warning' : 'blocker',
      message: `Missing sun/shadow screening input: ${input}.`,
    });
  }

  return findings;
}

function sumFinite(values: readonly (number | undefined)[]): number | undefined {
  const finiteValues = values.filter((value): value is number => isFiniteNumber(value));

  if (finiteValues.length === 0) {
    return undefined;
  }

  return finiteValues.reduce((total, value) => total + value, 0);
}

function getNumberRange(
  values: readonly (number | undefined)[],
): { min: number; max: number } | undefined {
  const finiteValues = values.filter((value): value is number => isFiniteNumber(value));

  if (finiteValues.length === 0) {
    return undefined;
  }

  return { min: Math.min(...finiteValues), max: Math.max(...finiteValues) };
}

function formatRange(range: { min: number; max: number }): string | number {
  return range.min === range.max ? range.min : `${range.min}-${range.max}`;
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function isFiniteNumber(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function roundTwo(value: number): number {
  return Math.round(value * 100) / 100;
}
